import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useSignIn, useSignUp, useAuth } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast,
  cn,
  Separator,
} from "@cliently/ui";
import {
  LogIn,
  ShieldCheck,
  UserPlus,
  KeyRound,
  ArrowLeft,
  Eye,
  EyeOff,
  Check,
  X,
  Loader2,
} from "lucide-react";
type OAuthStrategy = "oauth_google" | "oauth_apple";

interface ClerkError {
  errors?: Array<{
    code?: string;
    message?: string;
    longMessage?: string;
  }>;
}



const PasswordInput = ({
  showCriteria,
  breached,
  ...props
}: React.ComponentProps<typeof Input> & {
  id: string;
  showCriteria?: boolean;
  breached?: boolean;
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const value = typeof props.value === "string" ? props.value : "";

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          {...props}
          type={visible ? "text" : "password"}
          className="pr-10"
        />
        <Button
          type="button"
          tabIndex={-1}
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 shadow-none"
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>
      </div>
      {showCriteria && value.length > 0 && (
        <PasswordCriteria password={value} breached={breached} />
      )}
    </div>
  );
}



const PasswordCriteria = ({
  password,
  breached,
}: {
  password: string;
  breached?: boolean;
}) => {
  const { t } = useTranslation();

  const criterias = [
    {
      key: "minLength",
      met: password.length >= 8,
      label: t("portal.pwdMinLength"),
    },
    {
      key: "hasUpper",
      met: /[A-Z]/.test(password),
      label: t("portal.pwdUppercase"),
    },
    {
      key: "hasLower",
      met: /[a-z]/.test(password),
      label: t("portal.pwdLowercase"),
    },
    {
      key: "hasNumber",
      met: /\d/.test(password),
      label: t("portal.pwdNumber"),
    },
    {
      key: "hasSpecial",
      met: /[^A-Za-z0-9]/.test(password),
      label: t("portal.pwdSpecial"),
    },
    { key: "notBreached", met: !breached, label: t("portal.pwdNotBreached") },
  ];

  return (
    <ul className="space-y-1">
      {criterias.map((criteria) => (
        <li
          key={criteria.key}
          className={cn(
            "flex items-center gap-1.5 text-xs",
            criteria.met ? "text-emerald-500" : "text-destructive",
          )}
        >
          {criteria.met ? (
            <Check className="h-3 w-3" />
          ) : (
            <X className="h-3 w-3" />
          )}
          {criteria.label}
        </li>
      ))}
    </ul>
  );
}



const isBreachedError = (err: unknown): boolean => {
  const e = err as ClerkError;
  const code = e?.errors?.[0]?.code;
  const raw = e?.errors?.[0]?.message ?? "";
  return (
    code === "form_password_pwned" || raw.toLowerCase().includes("data breach")
  );
}



const translateClerkError = (err: unknown, t: (key: string) => string): string => {
  if (isBreachedError(err)) return "";

  const e = err as ClerkError;
  const code = e?.errors?.[0]?.code;

  if (
    code === "form_identifier_not_found" ||
    code === "form_password_incorrect" ||
    code === "form_password_validation_failed"
  ) {
    return t("portal.invalidCredentials");
  }

  if (code === "form_password_length_too_short") {
    return t("portal.pwdMinLength");
  }

  if (code === "form_identifier_exists") {
    return t("portal.alreadyHasAccount");
  }

  const raw =
    e?.errors?.[0]?.longMessage ??
    e?.errors?.[0]?.message ??
    (err instanceof Error ? err.message : undefined);

  return raw ?? t("portal.connectionError");
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `${(import.meta.env.VITE_API_URL as string).replace(/\/+$/, "")}/api`
  : "/api";



const apiPost = async <T = unknown,>(path: string, body: unknown): Promise<T> => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? "Erreur serveur");
  return data as T;
}

type ValidateResult =
  | {
      status: "no_account";
      clientId: string;
      firstName: string;
      lastName: string;
    }
  | { status: "has_account" }
  | { status: "new_client"; orgName: string };



export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slugFromUrl = searchParams.get("slug") ?? undefined;
  const { isSignedIn } = useAuth();
  const [activeTab, setActiveTab] = useState<"signin" | "first-time">(() =>
    slugFromUrl ? "first-time" : "signin",
  );

  if (isSignedIn) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="bg-muted/30 flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full">
            <ShieldCheck className="text-primary h-7 w-7" />
          </div>
          <CardTitle className="text-2xl">{t("portal.title")}</CardTitle>
          <CardDescription>{t("portal.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              if (v === "signin" || v === "first-time") setActiveTab(v);
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">
                <LogIn className="mr-2 h-4 w-4" />
                {t("portal.tabSignIn")}
              </TabsTrigger>
              <TabsTrigger value="first-time">
                <UserPlus className="mr-2 h-4 w-4" />
                {t("portal.tabFirstTime")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <SignInForm />
            </TabsContent>

            <TabsContent value="first-time">
              <FirstTimeForm
                onSwitchToSignIn={() => setActiveTab("signin")}
                registerSlug={slugFromUrl}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}



const SignInForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, setActive } = useSignIn();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [resetMode, setResetMode] = useState<boolean>(false);
  const [resetCode, setResetCode] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [resetSent, setResetSent] = useState<boolean>(false);
  const [resetBreached, setResetBreached] = useState<boolean>(false);
  const [secondFactorMode, setSecondFactorMode] = useState<boolean>(false);
  const [secondFactorCode, setSecondFactorCode] = useState<string>("");

  if (!signIn) return null;

  const handleOAuth = async (strategy: OAuthStrategy) => {
    setOauthLoading(strategy);
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: "/sso-callback",
        redirectUrlComplete: "/dashboard",
      });
    } catch {
      toast.error(t("portal.oauthError"));
      setOauthLoading(null);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signIn.create({
        identifier: email.trim(),
        password,
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive!({ session: result.createdSessionId });
        toast.success(t("portal.signInSuccess"));
        navigate("/dashboard");
      } else if (result.status === "needs_second_factor") {
        try {
          await signIn.prepareSecondFactor({ strategy: "email_code" });
          setSecondFactorMode(true);
          setSecondFactorCode("");
          toast.info(t("portal.2faDesc", { email: email.trim() }));
        } catch {
          toast.error(t("portal.signInIncomplete"));
        }
      } else {
        toast.error(t("portal.signInIncomplete"));
      }
    } catch (err: unknown) {
      const msg = translateClerkError(err, t);
      if (msg) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast.warning(t("portal.enterEmailFirst"));
      return;
    }
    setLoading(true);
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email.trim(),
      });
      setResetMode(true);
      setResetSent(true);
      toast.success(t("portal.resetCodeSent", { email }));
    } catch (err: unknown) {
      const e = err as ClerkError;
      toast.error(
        e?.errors?.[0]?.longMessage ??
          e?.errors?.[0]?.message ??
          t("portal.resetError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: resetCode.trim(),
        password: newPassword,
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive!({ session: result.createdSessionId });
        toast.success(t("portal.resetSuccess"));
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      if (isBreachedError(err)) {
        setResetBreached(true);
      }
      const msg = translateClerkError(err, t);
      if (msg) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSecondFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signIn.attemptSecondFactor({
        strategy: "email_code",
        code: secondFactorCode.trim(),
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive!({ session: result.createdSessionId });
        toast.success(t("portal.signInSuccess"));
        navigate("/dashboard");
      } else {
        toast.error(t("portal.signInIncomplete"));
      }
    } catch {
      toast.error(t("portal.2faError"));
    } finally {
      setLoading(false);
    }
  };

  if (secondFactorMode) {
    return (
      <form onSubmit={handleSecondFactor} className="space-y-4">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground h-auto gap-1 p-0 text-sm font-normal shadow-none"
          onClick={() => {
            setSecondFactorMode(false);
            setSecondFactorCode("");
          }}
        >
          <ArrowLeft className="h-3 w-3" />
          {t("portal.backToSignIn")}
        </Button>

        <div className="bg-muted/30 rounded-lg border p-4 text-center">
          <div className="bg-primary/10 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full">
            <ShieldCheck className="text-primary h-6 w-6" />
          </div>
          <p className="text-sm font-medium">{t("portal.2faTitle")}</p>
          <p className="text-muted-foreground mt-1 text-xs">
            {t("portal.2faDesc", { email: email.trim() })}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="2fa-code">{t("portal.2faCode")}</Label>
          <Input
            id="2fa-code"
            type="text"
            inputMode="numeric"
            value={secondFactorCode}
            onChange={(e) => setSecondFactorCode(e.target.value)}
            placeholder="123456"
            required
            autoFocus
            className="text-center text-lg tracking-widest"
            maxLength={6}
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={loading || secondFactorCode.length < 4}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <ShieldCheck className="mr-2 h-4 w-4" />
              {t("portal.2faSubmit")}
            </>
          )}
        </Button>
      </form>
    );
  }

  if (resetMode) {
    return (
      <form onSubmit={handleResetPassword} className="space-y-4">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground h-auto gap-1 p-0 text-sm font-normal shadow-none"
          onClick={() => {
            setResetMode(false);
            setResetSent(false);
          }}
        >
          <ArrowLeft className="h-3 w-3" />
          {t("portal.backToSignIn")}
        </Button>

        {resetSent && (
          <p className="text-muted-foreground text-sm">
            {t("portal.resetCodeSent", { email })}
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="reset-code">{t("portal.resetCode")}</Label>
          <Input
            id="reset-code"
            type="text"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            placeholder="123456"
            required
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-password">{t("portal.newPassword")}</Label>
          <PasswordInput
            id="new-password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setResetBreached(false);
            }}
            placeholder="••••••••"
            required
            showCriteria
            breached={resetBreached}
          />
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
          ) : (
            <>
              <KeyRound className="mr-2 h-4 w-4" />
              {t("portal.resetSubmit")}
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSignIn} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="signin-email">{t("portal.email")}</Label>
        <Input
          id="signin-email"
          type="email"
          placeholder={t("portal.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="signin-password">{t("portal.password")}</Label>
          <Button
            type="button"
            variant="link"
            className="text-primary h-auto p-0 text-xs"
            onClick={handleForgotPassword}
          >
            {t("portal.forgotPassword")}
          </Button>
        </div>
        <PasswordInput
          id="signin-password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={loading || !email.trim() || !password}
      >
        {loading ? (
          <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
        ) : (
          <>
            <LogIn className="mr-2 h-4 w-4" />
            {t("portal.signInSubmit")}
          </>
        )}
      </Button>

      <div className="relative my-2">
        <Separator />
        <span className="bg-card text-muted-foreground absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs">
          {t("portal.or")}
        </span>
      </div>

      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={!!oauthLoading}
          onClick={() => handleOAuth("oauth_google")}
        >
          {oauthLoading === "oauth_google" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          {t("portal.continueWithGoogle")}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={!!oauthLoading}
          onClick={() => handleOAuth("oauth_apple")}
        >
          {oauthLoading === "oauth_apple" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg
              className="mr-2 h-4 w-4"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-2.12 4.54-3.74 4.25z" />
            </svg>
          )}
          {t("portal.continueWithApple")}
        </Button>
      </div>
    </form>
  );
}



const FirstTimeForm = ({
  onSwitchToSignIn,
  registerSlug,
}: {
  onSwitchToSignIn: () => void;
  registerSlug?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signUp, setActive } = useSignUp();
  const [step, setStep] = useState<"validate" | "signup" | "verify-email">(
    "validate",
  );
  const [accessCode, setAccessCode] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isNewClient, setIsNewClient] = useState<boolean>(false);
  const [pwdBreached, setPwdBreached] = useState<boolean>(false);

  if (!signUp) return null;

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = registerSlug
        ? await apiPost<ValidateResult>("/portal/validate-access-by-slug", {
            slug: registerSlug.trim(),
            email: email.trim(),
          })
        : await apiPost<ValidateResult>("/portal/validate-access", {
            accessCode: accessCode.trim(),
            email: email.trim(),
          });

      if (result.status === "has_account") {
        toast.info(t("portal.alreadyHasAccount"));
        setTimeout(onSwitchToSignIn, 2000);
        return;
      }

      if (result.status === "new_client") {
        setIsNewClient(true);
        setFirstName("");
        setLastName("");
      } else {
        setIsNewClient(false);
        setFirstName(result.firstName);
        setLastName(result.lastName);
      }

      setStep("signup");
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : t("portal.connectionError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const prefix = (email.trim().split("@")[0] ?? "client").replace(
        /[^a-zA-Z0-9_]/g,
        "_",
      );
      const username = `${prefix}_${Math.random().toString(36).slice(2, 6)}`;

      const result = await signUp.create({
        emailAddress: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username,
      });

      if (result.status === "complete") {
        try {
          if (registerSlug) {
            await apiPost("/portal/link-account-by-slug", {
              slug: registerSlug.trim(),
              email: email.trim(),
              clerkUserId: result.createdUserId,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
            });
          } else {
            await apiPost("/portal/link-account", {
              accessCode: accessCode.trim(),
              email: email.trim(),
              clerkUserId: result.createdUserId,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
            });
          }
        } catch (linkErr: unknown) {
          console.error("link-account failed:", linkErr);
        }
        if (result.createdSessionId) {
          await setActive!({ session: result.createdSessionId });
        }
        toast.success(t("portal.accountCreated"));
        navigate("/dashboard");
      } else if (
        result.status === "missing_requirements" &&
        result.unverifiedFields?.includes("email_address")
      ) {
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });
        toast.info(t("portal.verificationSent", { email }));
        setStep("verify-email");
      } else {
        toast.error(t("portal.signUpIncomplete"));
      }
    } catch (err: unknown) {
      if (isBreachedError(err)) {
        setPwdBreached(true);
      }
      const msg = translateClerkError(err, t);
      if (msg) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (step === "verify-email") {
    return (
      <EmailVerificationStep
        signUp={signUp}
        setActive={setActive!}
        accessCode={accessCode}
        registerSlug={registerSlug}
        email={email}
        firstName={firstName}
        lastName={lastName}
        onSwitchToSignIn={onSwitchToSignIn}
      />
    );
  }

  if (step === "signup") {
    return (
      <form onSubmit={handleSignUp} className="space-y-4">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground h-auto gap-1 p-0 text-sm font-normal shadow-none"
          onClick={() => setStep("validate")}
        >
          <ArrowLeft className="h-3 w-3" />
          {t("portal.back")}
        </Button>

        {!isNewClient && (
          <p className="text-muted-foreground text-sm">
            {t("portal.welcomeClient", {
              name: `${firstName} ${lastName}`.trim(),
            })}
          </p>
        )}

        {isNewClient && (
          <p className="text-muted-foreground text-sm">
            {t("portal.newClientWelcome")}
          </p>
        )}

        {isNewClient && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="signup-first">{t("portal.firstName")}</Label>
              <Input
                id="signup-first"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-last">{t("portal.lastName")}</Label>
              <Input
                id="signup-last"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>{t("portal.email")}</Label>
          <Input type="email" value={email} disabled className="bg-muted" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="signup-password">{t("portal.choosePassword")}</Label>
          <PasswordInput
            id="signup-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPwdBreached(false);
            }}
            required
            autoFocus={!isNewClient}
            showCriteria
            breached={pwdBreached}
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={
            loading ||
            !password ||
            (isNewClient && (!firstName.trim() || !lastName.trim()))
          }
        >
          {loading ? (
            <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
          ) : (
            <>
              <UserPlus className="mr-2 h-4 w-4" />
              {t("portal.createAccount")}
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleValidate} className="space-y-4">
      {!registerSlug && (
        <div className="space-y-2">
          <Label htmlFor="access-code">{t("portal.accessCode")}</Label>
          <Input
            id="access-code"
            type="text"
            placeholder={t("portal.placeholder")}
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            className="font-mono tracking-widest"
            autoFocus
            required
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="first-email">{t("portal.email")}</Label>
        <Input
          id="first-email"
          type="email"
          placeholder={t("portal.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus={!!registerSlug}
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={
          loading || !email.trim() || (!registerSlug && !accessCode.trim())
        }
      >
        {loading ? (
          <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
        ) : (
          t("portal.verify")
        )}
      </Button>
    </form>
  );
}



const EmailVerificationStep = ({
  signUp,
  setActive,
  accessCode,
  registerSlug,
  email,
  firstName,
  lastName,
  onSwitchToSignIn,
}: {
  signUp: NonNullable<ReturnType<typeof useSignUp>["signUp"]>;
  setActive: (p: { session: string }) => Promise<void>;
  accessCode: string;
  registerSlug?: string;
  email: string;
  firstName: string;
  lastName: string;
  onSwitchToSignIn: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const linkAndRedirect = async (
    userId?: string | null,
    sessionId?: string | null,
  ) => {
    try {
      if (registerSlug) {
        await apiPost("/portal/link-account-by-slug", {
          slug: registerSlug.trim(),
          email: email.trim(),
          clerkUserId: userId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      } else {
        await apiPost("/portal/link-account", {
          accessCode: accessCode.trim(),
          email: email.trim(),
          clerkUserId: userId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      }
    } catch (linkErr: unknown) {
      console.error("link-account failed:", linkErr);
    }

    if (sessionId) {
      await setActive({ session: sessionId });
      toast.success(t("portal.accountCreated"));
      navigate("/dashboard");
    } else {
      toast.success(t("portal.accountCreatedSignIn"));
      onSwitchToSignIn();
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: code.trim(),
      });

      await linkAndRedirect(
        result.createdUserId ?? signUp.createdUserId,
        result.createdSessionId ?? signUp.createdSessionId,
      );
    } catch (err: unknown) {
      const e = err as ClerkError;
      const errCode = e?.errors?.[0]?.code;

      if (errCode === "verification_already_verified") {
        await linkAndRedirect(signUp.createdUserId, signUp.createdSessionId);
        return;
      }

      toast.error(
        e?.errors?.[0]?.longMessage ??
          e?.errors?.[0]?.message ??
          t("portal.verificationError"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleVerify} className="space-y-4">
      <p className="text-muted-foreground text-sm">
        {t("portal.verificationSent", { email })}
      </p>

      <div className="space-y-2">
        <Label htmlFor="verify-code">{t("portal.verificationCode")}</Label>
        <Input
          id="verify-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          required
          autoFocus
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={loading || !code.trim()}
      >
        {loading ? (
          <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
        ) : (
          t("portal.verifyEmail")
        )}
      </Button>
    </form>
  );
}
